// frontend/src/pages/WorkflowPage.jsx
/**
 * Workflow/Pipeline View - Kanban-style board for tracking client status
 * Users see their assigned clients, managers can toggle between all employees
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPatch } from '../api';
import { loadFilterPrefs, saveFilterPrefs } from './workflow/filterPrefs';
import { getStatusAge } from './workflow/statusAge';
import {
  WORKFLOW_STRUCTURE, LAST_COLUMN_ID, getColumnForStatus, isCompletedStatus,
} from './workflow/columns';
import { WORKFLOW_CATEGORIES, CATEGORY_KEYS, categoryOf } from './workflow/categories';
import RollForwardFlow from './tasks/RollForwardFlow';

export default function WorkflowPage({ user }) {
  const navigate = useNavigate();
  
  // State
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => ({
    ...loadFilterPrefs(user?.id),
    search: '',   // never persisted -- see filterPrefs.js
  }));
  // Preliminary/planned work is off by default: it is all Not Started, so
  // showing it unasked would pile forecast jobs into the first column and
  // bury the live ones. Not persisted -- it is a "let me peek ahead" mode,
  // not a lasting preference, and a board silently showing forecast work on
  // return would be worse than re-ticking it.
  const [showPlanned, setShowPlanned] = useState(false);
  const [draggedCard, setDraggedCard] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  // The task whose completion just opened the roll-forward, or null.
  const [rollTask, setRollTask] = useState(null);
  const [pendingDrop, setPendingDrop] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  // Load employees for filter dropdown
  useEffect(() => {
    loadEmployees();
  }, []);

  // filterPrefs.js can't validate `employee` against real ids -- it has no
  // access to the employee list -- so a saved id for a since-deactivated
  // employee passes through unchanged. Once the real list is in, the select
  // would render with nothing chosen while getClientsForColumn keeps
  // filtering on the stale id: an empty board that looks unfiltered. Reset
  // to 'all' only once we actually know the valid ids, so this can't clobber
  // a legitimate selection made before the list arrives.
  useEffect(() => {
    if (employees.length === 0) return;
    if (filters.employee === 'all') return;
    const stillExists = employees.some(
      (emp) => String(emp.id) === String(filters.employee)
    );
    if (!stillExists) {
      setFilters((prev) => ({ ...prev, employee: 'all' }));
    }
  }, [employees, filters.employee]);

  // Load the cards. Every filter (employee, category ticks, sort) is applied
  // client-side, so only "Show planned" changes what is fetched -- refetching
  // on each tick blanked the board to "Loading..." for nothing.
  useEffect(() => {
    loadClients();
  }, [showPlanned]);

  // Persist the sticky filters. Runs on the initial render too, which simply
  // rewrites what was just read -- harmless, and cheaper than tracking whether
  // this is the first pass.
  useEffect(() => {
    saveFilterPrefs(filters);
  }, [filters]);

  const loadEmployees = async () => {
    try {
      const data = await apiGet('/workflow/employees/');
      setEmployees(data.employees || []);
    } catch (error) {
      console.error('Failed to load employees:', error);
    }
  };

  const loadClients = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      // Every kind of work is fetched; the category tick boxes are applied
      // client-side in getClientsForColumn (Bookkeeping vs Other is a split
      // of one API kind, so the server-side task_type filter cannot express
      // it). Finished work is left out: there is no Completed column.
      // NOTE: search is still a no-op on this page (follow-up). The employee
      // filter is applied client-side too, against assigned_employee_ids.
      params.append('hide_completed', '1');
      if (showPlanned) params.append('include_planned', '1');
      const qs = params.toString();
      const items = await apiGet(`/api/workflow/items/${qs ? `?${qs}` : ''}`);

      // Normalize each item to the shape the rest of the page expects:
      // - id: numeric (extracted from the 'task-N' / 'other-task-N' string),
      //       used for the PATCH endpoint
      // - card_id: original prefixed id string (kept for any future need)
      // - kind: 'task' | 'other-task'
      // - is_other_task: convenience flag for updateClientStatus routing
      // - status: STRING (name) — getColumnForStatus expects a name
      // - status_id, due_date, fiscal_year, client_name preserved for display
      const list = Array.isArray(items) ? items : [];
      const normalized = list.map((it) => {
        const numericId = parseInt(
          String(it.id).replace(/^(other-)?task-/, ''),
          10
        );
        return {
          id: numericId,
          card_id: it.id,
          kind: it.kind,
          is_other_task: it.kind === 'other-task',
          name: it.name || '',
          status: it.status?.name || '',
          status_id: it.status?.id || null,
          due_date: it.due_date || null,
          fiscal_year: it.fiscal_year || null,
          client_name: it.client?.name || '',
          client_id: it.client?.id || null,
          task_type: it.task_type,
          // The TaskType / OtherTaskType name -- categoryOf() reads it to
          // tell Bookkeeping from Other.
          task_type_name: it.task_type_name || '',
          is_completed: Boolean(it.is_completed),
          // Null is a real answer, not a missing one — a task whose status has
          // never moved since the writer was added shows no chip at all.
          last_status_changed_at: it.last_status_changed_at || null,
          is_planned: Boolean(it.is_planned),
          assigned_employee_ids: it.assigned_employee_ids || [],
        };
      });
      setClients(normalized);
    } catch (error) {
      console.error('Failed to load workflow items:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group clients by column and apply sort
  const getClientsForColumn = (column) => {
    const filtered = clients.filter(client => {
      // Belt and braces with the API's hide_completed: a finished card has
      // no column to sit in.
      if (client.is_completed || isCompletedStatus(client.status)) return false;
      if (!filters.types.includes(categoryOf(client))) return false;
      const clientColumn = getColumnForStatus(client.status);
      if (clientColumn?.id !== column.id) return false;
      // Employee filter (manager-only dropdown). '' (non-manager) and 'all'
      // both mean "no filter"; otherwise keep only cards assigned to that user.
      if (filters.employee && filters.employee !== 'all') {
        const empId = Number(filters.employee);
        if (!(client.assigned_employee_ids || []).includes(empId)) return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (filters.sortBy === 'due_date') {
        // Nulls go to the bottom
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      }
      // 'docs_received' sort is no longer supported — the new
      // /api/workflow/items/ endpoint does not expose docs_received_at.
      // Fall through to alphabetical sort.
      // Default: alphabetical by name
      return (a.name || '').localeCompare(b.name || '');
    });
  };

  const handleDragStart = (e, client) => {
    setDraggedCard(client);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = async (e, targetColumn) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (!draggedCard) return;

    const currentColumn = getColumnForStatus(draggedCard.status);
    
    // If dropped in same column, do nothing
    if (currentColumn?.id === targetColumn.id) {
      setDraggedCard(null);
      return;
    }

    // If target column has multiple statuses, show modal
    if (targetColumn.statuses.length > 1) {
      setPendingDrop({ client: draggedCard, targetColumn });
      setShowStatusModal(true);
    } else {
      // Only one status, assign it directly
      await updateClientStatus(draggedCard, targetColumn.statuses[0]);
    }

    setDraggedCard(null);
  };

  const updateClientStatus = async (client, statusName) => {
    try {
      // Get all statuses to find the ID
      const statusesData = await apiGet('/status-options/by_type/');
      const allStatuses = statusesData.general || [];
      
      // Find status by name (case-insensitive)
      const targetStatus = allStatuses.find(
        s => s.name.toLowerCase() === statusName.toLowerCase()
      );

      if (!targetStatus) {
        console.error('Could not find target status:', statusName);
        alert(`Could not find status: ${statusName}`);
        return;
      }

      // Update status — route to /api/other-tasks/ if this is an OtherTask
      // card, otherwise to /api/tasks/ (the new Task model endpoint).
      const endpoint = client.is_other_task
        ? `/api/other-tasks/${client.id}/`
        : `/api/tasks/${client.id}/`;

      // A Task moved to "Completed" is finished exactly as the task page's
      // checkmark finishes it: the boolean goes with the status (the server
      // refuses to roll an open task forward), and the roll-forward is
      // offered once it is saved. Other tasks have their own repeat rule.
      const completing =
        !client.is_other_task &&
        targetStatus.name.trim().toLowerCase() === 'completed';
      await apiPatch(endpoint, completing
        ? { status: targetStatus.id, is_completed: true }
        : { status: targetStatus.id });

      // Reload clients to show updated data
      await loadClients();
      if (completing) {
        setRollTask({
          id: client.id, client: client.client_id,
          client_name: client.client_name, fiscal_year: client.fiscal_year,
        });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update client status');
    }
  };

  // The Complete button on a last-column card. Exactly the old "drop into
  // Completed and pick Completed" path -- same PATCH (status + is_completed
  // for a Task), same roll-forward pop-up -- so the board has one way to
  // finish a file, not two that could drift. The Completed column (and its
  // manager-only "Done -- Archive" button) went with the firm's 2026-10-30
  // feedback; archiving lives in the roll-forward pop-up and Archived page.
  const handleComplete = (client) => updateClientStatus(client, 'Completed');

  // The category tick boxes. "All" is derived, never stored: it shows ticked
  // exactly when every box is, and ticking it ticks (or clears) them all.
  const allTypesTicked = CATEGORY_KEYS.every((k) => filters.types.includes(k));
  const toggleAllTypes = () =>
    setFilters((prev) => ({ ...prev, types: allTypesTicked ? [] : [...CATEGORY_KEYS] }));
  const toggleType = (key) =>
    setFilters((prev) => {
      const has = prev.types.includes(key);
      const next = has ? prev.types.filter((k) => k !== key) : [...prev.types, key];
      return { ...prev, types: CATEGORY_KEYS.filter((k) => next.includes(k)) };
    });

  const handleStatusSelect = async (statusName) => {
    if (pendingDrop) {
      await updateClientStatus(pendingDrop.client, statusName);
    }
    setShowStatusModal(false);
    setPendingDrop(null);
  };

  const handleCardClick = (cardId, isOtherTask) => {
    if (isOtherTask) {
      navigate(`/other-tasks/${cardId}`);
    } else {
      navigate(`/tasks/${cardId}`);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      background: '#f6f7fb',
      fontFamily: 'Figtree, -apple-system, sans-serif',
    }}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; max-height: 0; }
          to { opacity: 1; max-height: 100px; }
        }
      `}</style>
      {/* Header */}
      <div style={{
        background: '#ffffff',
        borderBottom: '1px solid #d0d4e4',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Workflow</h1>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {/* Search */}
          <input
            type="text"
            placeholder="Search clients..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{
              padding: '6px 10px',
              border: '1px solid #d0d4e4',
              borderRadius: 6,
              fontSize: 13,
              width: 180,
            }}
          />

          {/* Sort By */}
          <select
            value={filters.sortBy}
            onChange={(e) => setFilters({ ...filters, sortBy: e.target.value })}
            style={{
              padding: '6px 10px',
              border: '1px solid #d0d4e4',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            <option value="name">Sort: Name</option>
            <option value="due_date">Sort: Due Date</option>
          </select>

          {/* What you can see: tick boxes, not a dropdown (firm, 2026-10-30). */}
          <div
            role="group"
            aria-label="Show work"
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '4px 10px', border: '1px solid #d0d4e4', borderRadius: 6,
              fontSize: 13, color: '#374151', whiteSpace: 'nowrap',
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontWeight: 600 }}>
              <input type="checkbox" checked={allTypesTicked} onChange={toggleAllTypes} />
              All
            </label>
            {WORKFLOW_CATEGORIES.map((cat) => (
              <label key={cat.key} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={filters.types.includes(cat.key)}
                  onChange={() => toggleType(cat.key)}
                />
                {cat.label}
              </label>
            ))}
          </div>

          {/* Employee filter — available to everyone */}
          {employees.length > 0 && (
            <select
              value={filters.employee}
              onChange={(e) => setFilters({ ...filters, employee: e.target.value })}
              style={{
                padding: '6px 10px',
                border: '1px solid #d0d4e4',
                borderRadius: 6,
                fontSize: 13,
                minWidth: 140,
              }}
            >
              <option value="all">All Employees</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
          )}

          {/* Preliminary work, off by default. Marked on the card so forecast
              is never mistaken for something actually in flight. */}
          <label
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 13, color: '#374151', whiteSpace: 'nowrap',
              cursor: 'pointer',
            }}
            title="Include preliminary tasks that are planned but not yet started"
          >
            <input
              type="checkbox"
              checked={showPlanned}
              onChange={(e) => setShowPlanned(e.target.checked)}
            />
            Show planned
          </label>
        </div>
      </div>

      {/* Pipeline Board */}
      <div style={{
        flex: 1,
        overflowX: 'auto',
        overflowY: 'hidden',
        padding: 16,
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
            Loading workflow...
          </div>
        ) : (
          <div style={{
            display: 'flex',
            gap: 12,
            minWidth: 'max-content',
            height: '100%',
          }}>
            {WORKFLOW_STRUCTURE.map(column => {
              const columnClients = getClientsForColumn(column);
              
              return (
                <div
                  key={column.id}
                  onDragOver={(e) => handleDragOver(e, column.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, column)}
                  style={{
                    flex: '0 0 210px',
                    display: 'flex',
                    flexDirection: 'column',
                    background: '#ffffff',
                    borderRadius: 10,
                    border: `2px solid ${dragOverColumn === column.id ? column.color : '#e5e7eb'}`,
                    transition: 'border-color 0.2s',
                    maxHeight: '100%',
                  }}
                >
                  {/* Column Header */}
                  <div style={{
                    padding: '12px 14px',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 16 }}>{column.icon}</span>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{column.name}</span>
                    </div>
                    <span style={{
                      background: column.color,
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: 10,
                      fontSize: 11,
                      fontWeight: 600,
                    }}>
                      {columnClients.length}
                    </span>
                  </div>

                  {/* Cards */}
                  <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}>
                    {columnClients.length === 0 ? (
                      <div style={{
                        textAlign: 'center',
                        padding: 24,
                        color: '#9ca3af',
                        fontSize: 12,
                      }}>
                        No clients
                      </div>
                    ) : (
                      columnClients.map(client => {
                        const isHovered = hoveredCard === client.card_id;
                        return (
                        <div
                          key={client.card_id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, client)}
                          onClick={(e) => {
                            if (!e.defaultPrevented) {
                              handleCardClick(client.id, client.is_other_task);
                            }
                          }}
                          onMouseEnter={() => setHoveredCard(client.card_id)}
                          onMouseLeave={() => setHoveredCard(null)}
                          style={{
                            background: isHovered ? '#f3f4f6' : '#f9fafb',
                            border: `1px solid ${isHovered ? column.color + '60' : '#e5e7eb'}`,
                            borderRadius: 6,
                            padding: '8px 10px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: isHovered ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                            transform: isHovered ? 'translateY(-1px)' : 'none',
                          }}
                        >
                          {/* Task name + client (when a Task is tied to a client) */}
                          <div>
                            <div style={{
                              fontWeight: 600,
                              fontSize: 12,
                              color: '#111827',
                              lineHeight: '1.3',
                            }}>
                              {client.name}
                            </div>
                            {client.is_planned && (
                              <span
                                title="Planned — not started yet"
                                style={{
                                  display: 'inline-block',
                                  fontSize: 9,
                                  fontWeight: 700,
                                  letterSpacing: '0.04em',
                                  color: '#6d28d9',
                                  background: '#ede9fe',
                                  border: '1px solid #ddd6fe',
                                  borderRadius: 4,
                                  padding: '1px 5px',
                                  marginBottom: 3,
                                }}
                              >
                                PLANNED
                              </span>
                            )}
                            {client.client_name && (
                              <div style={{
                                fontSize: 10,
                                fontWeight: 500,
                                color: '#6b7280',
                                marginTop: 2,
                              }}>
                                {client.client_name}
                              </div>
                            )}
                            {(() => {
                              const timeAgo = getStatusAge(client.last_status_changed_at);
                              return timeAgo ? (
                                <div style={{
                                  fontSize: 10,
                                  fontWeight: 500,
                                  color: timeAgo.color,
                                  marginTop: 2,
                                }}>
                                  Status changed: {timeAgo.label}
                                </div>
                              ) : null;
                            })()}
                          </div>

                          {/* Hover expansion — due date & fiscal year */}
                          {isHovered && (
                            <div style={{
                              marginTop: 6,
                              paddingTop: 6,
                              borderTop: '1px solid #e5e7eb',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 3,
                              animation: 'fadeIn 0.15s ease',
                            }}>
                              {client.due_date && (
                                <div style={{ fontSize: 10, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <span>📅</span>
                                  <span>Due: {client.due_date}</span>
                                </div>
                              )}
                              {client.fiscal_year && (
                                <div style={{ fontSize: 10, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <span>📆</span>
                                  <span>FY: {client.fiscal_year}</span>
                                </div>
                              )}
                              {!client.due_date && !client.fiscal_year && (
                                <div style={{ fontSize: 10, color: '#9ca3af', fontStyle: 'italic' }}>
                                  No additional details
                                </div>
                              )}
                            </div>
                          )}

                          {/* Complete — the last step's way out. Replaces the
                              old Completed column. */}
                          {column.id === LAST_COLUMN_ID && (
                            <button
                              type="button"
                              draggable={false}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleComplete(client);
                              }}
                              style={{
                                marginTop: 8,
                                width: '100%',
                                padding: '5px 8px',
                                border: '1px solid #10b981',
                                borderRadius: 5,
                                background: '#ecfdf5',
                                color: '#047857',
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 5,
                              }}
                              title="Mark this task completed"
                            >
                              ✓ Complete
                            </button>
                          )}
                        </div>
                      );})
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Status Selection Modal */}
      {rollTask && (
        <RollForwardFlow
          task={rollTask}
          onClose={() => setRollTask(null)}
          onRolled={async () => {
            setRollTask(null);
            await loadClients();
          }}
        />
      )}

      {showStatusModal && pendingDrop && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => {
            setShowStatusModal(false);
            setPendingDrop(null);
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: 12,
              padding: 24,
              minWidth: 300,
              maxWidth: 400,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', fontSize: 16, fontWeight: 600 }}>
              Select Status for {pendingDrop.targetColumn.name}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {pendingDrop.targetColumn.statuses.map(status => (
                <button
                  key={status}
                  onClick={() => handleStatusSelect(status)}
                  style={{
                    padding: '10px 16px',
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                    textAlign: 'left',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = pendingDrop.targetColumn.color;
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.borderColor = pendingDrop.targetColumn.color;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.color = 'inherit';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  {status}
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                setShowStatusModal(false);
                setPendingDrop(null);
              }}
              style={{
                marginTop: 12,
                width: '100%',
                padding: '8px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                color: '#6b7280',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}